import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Modal,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { gasProviderInfoService } from '../../../../services/gasProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('gasProviderInput');

export const GasProviderInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { gasProviderId?: string; isEdit?: boolean }>, string>>();
  const { gasProviderId, isEdit } = route.params || {};
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showBillingCycleModal, setShowBillingCycleModal] = useState(false);
  const [showAutopayMethodModal, setShowAutopayMethodModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [savedGasProviderId, setSavedGasProviderId] = useState<string | undefined>();
  
  const [formData, setFormData] = useState({
    companyName: '',
    accountNumber: '',
    serviceAddress: '',
    billingAddress: '',
    customerServicePhone: '',
    emergencyPhone: '',
    website: '',
    billingCycle: '',
    monthlyCost: '',
    autopayEnabled: false,
    autopayMethod: '',
    meterNumber: '',
    shutoffLocation: '',
    notes: '',
    username: '',
    password: '',
  });

  const billingCycles = ['Monthly', 'Quarterly', 'Annually', 'Other'];
  const autopayMethods = ['Bank Account', 'Credit Card', 'Debit Card', 'Other'];

  // Load existing gas provider data when editing
  useEffect(() => {
    logger.debug('[Edit Debug] useEffect triggered - isEdit:', isEdit, 'gasProviderId:', gasProviderId);
    if (isEdit && gasProviderId) {
      logger.debug('[Edit Debug] Loading gas provider data for editing');
      loadGasProviderData();
    }
  }, [isEdit, gasProviderId]);

  const loadGasProviderData = async () => {
    try {
      logger.debug('[Edit Debug] loadGasProviderData called with ID:', gasProviderId);
      if (!gasProviderId) {
        logger.debug('[Edit Debug] No gasProviderId provided, returning');
        return;
      }
      
      logger.debug('[Edit Debug] Fetching data for ID:', gasProviderId);
      const data = await gasProviderInfoService.getGasProviderInfoById(gasProviderId);
      logger.debug('[Edit Debug] Received data:', data);
      
      if (data) {
        setFormData({
          companyName: data.companyName || '',
          accountNumber: data.accountNumber || '',
          serviceAddress: data.serviceAddress || '',
          billingAddress: data.billingAddress || '',
          customerServicePhone: formatPhoneNumber(data.customerServicePhone || ''),
          emergencyPhone: formatPhoneNumber(data.emergencyPhone || ''),
          website: data.website || '',
          billingCycle: data.billingCycle || '',
          monthlyCost: data.monthlyCost ? formatCurrency(data.monthlyCost.toString()) : '',
          autopayEnabled: data.autopayEnabled || false,
          autopayMethod: data.autopayMethod || '',
          meterNumber: data.meterNumber || '',
          shutoffLocation: data.shutoffLocation || '',
          notes: data.notes || '',
          username: '',
          password: '',
        });

        // Load portal credentials
        try {
          const portalData = await vaultService.getPortalCredentials(gasProviderId, 'gas-provider');
          if (portalData) {
            setFormData(prev => ({
              ...prev,
              username: portalData.username || '',
              password: portalData.password || '',
              accountNumber: portalData.accountNumber || prev.accountNumber,
            }));
            setHasOnlinePortal(true);
          }
        } catch (vaultError) {
          logger.debug('Could not load portal credentials:', vaultError);
        }
      }
    } catch (error) {
      logger.error('Error loading gas provider data:', error);
      Alert.alert('Error', 'Failed to load gas provider information');
    }
  };

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  const handlePhoneChange = (field: string, text: string) => {
    const formatted = formatPhoneNumber(text);
    setFormData({ ...formData, [field]: formatted });
  };

  const handleCurrencyChange = (text: string) => {
    const cleaned = text.replace(/^\$/, '');
    setFormData({ ...formData, monthlyCost: formatCurrency(cleaned) });
  };

  const handleBillingCycleSelect = (cycle: string) => {
    setFormData({ ...formData, billingCycle: cycle });
    setShowBillingCycleModal(false);
  };

  const handleAutopayMethodSelect = (method: string) => {
    setFormData({ ...formData, autopayMethod: method });
    setShowAutopayMethodModal(false);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.accountNumber) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      
      // Basic validation
      if (!formData.companyName) {
        Alert.alert('Validation Error', 'Company name is required');
        setSubmitting(false);
        return;
      }

      // Prepare payload for API
      const payload: any = {
        companyName: formData.companyName,
        serviceAddress: formData.serviceAddress || undefined,
        billingAddress: formData.billingAddress || undefined,
        customerServicePhone: formData.customerServicePhone.replace(/\D/g, '') || undefined,
        emergencyPhone: formData.emergencyPhone.replace(/\D/g, '') || undefined,
        website: formData.website || undefined,
        billingCycle: formData.billingCycle || undefined,
        monthlyCost: formData.monthlyCost ? parseFloat(formData.monthlyCost.replace(/[^0-9.]/g, '')) : undefined,
        autopayEnabled: formData.autopayEnabled,
        autopayMethod: formData.autopayMethod || undefined,
        meterNumber: formData.meterNumber || undefined,
        shutoffLocation: formData.shutoffLocation || undefined,
        notes: formData.notes || undefined,
      };

      // Create or update gas provider record
      let saved;
      let savedId;

      if (isEdit && gasProviderId) {
        saved = await gasProviderInfoService.updateGasProviderInfo(gasProviderId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = saved?.data || saved;
        savedId = updatedData.id || gasProviderId;
      } else {
        saved = await gasProviderInfoService.createGasProviderInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }
      
      // Store the saved ID for the Done button
      setSavedGasProviderId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password || formData.accountNumber)) {
        try {
          await vaultService.storePortalCredentials(savedId, 'gas-provider', {
            username: formData.username,
            password: formData.password,
            accountNumber: formData.accountNumber,
          });
        } catch (e) {
          logger.error('Vault write failed for gas provider portal credentials:', e);
        }
      }

      if (isEdit) {
        // For edit mode, navigate to lock/unlock screen
        Alert.alert('Success', 'Gas provider information updated successfully!', [
          {
            text: 'OK',
            onPress: () => navigation.navigate('GasProviderLockUnlock', { gasProviderId: gasProviderId })
          }
        ]);
      } else {
        // Clear the form and reset states for Add Another
        setFormData({
          companyName: '',
          accountNumber: '',
          serviceAddress: '',
          billingAddress: '',
          customerServicePhone: '',
          emergencyPhone: '',
          website: '',
          billingCycle: '',
          monthlyCost: '',
          autopayEnabled: false,
          autopayMethod: '',
          meterNumber: '',
          shutoffLocation: '',
          notes: '',
          username: '',
          password: '',
        });
        setHasOnlinePortal(false);
        
        // Show Add Another / Done buttons after successful save
        setShowAddAnotherDone(true);
        // Reset submitting so buttons are interactive
        setSubmitting(false);
      }
    } catch (error: any) {
      logger.error('Error saving gas provider info:', error);
      Alert.alert('Error', 'Failed to save gas provider information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      companyName: '',
      accountNumber: '',
      serviceAddress: '',
      billingAddress: '',
      customerServicePhone: '',
      emergencyPhone: '',
      website: '',
      billingCycle: '',
      monthlyCost: '',
      autopayEnabled: false,
      autopayMethod: '',
      meterNumber: '',
      shutoffLocation: '',
      notes: '',
      username: '',
      password: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    logger.debug('[Done Debug] Using ID:', savedGasProviderId);
    (navigation as any).navigate('GasProviderLockUnlock', { gasProviderId: savedGasProviderId });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Gas Provider Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Company Name *</Text>
            <TextInput
              style={styles.input}
              value={formData.companyName}
              onChangeText={(text) => setFormData({ ...formData, companyName: text })}
              placeholder="Ex: City Gas Company"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Account Number 🔒</Text>
            <View style={styles.passwordContainer}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                value={formData.accountNumber}
                onChangeText={(text) => setFormData({ ...formData, accountNumber: text })}
                placeholder="Enter Account Number"
                placeholderTextColor="#A9A9A9"
                secureTextEntry={!showAccountNumber}
              />
              <TouchableOpacity
                style={styles.eyeButton}
                onPress={() => setShowAccountNumber(!showAccountNumber)}
              >
                <Image
                  source={showAccountNumber ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                  style={styles.eyeIcon}
                />
              </TouchableOpacity>
            </View>
            <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Service Address</Text>
            <TextInput
              style={styles.input}
              value={formData.serviceAddress}
              onChangeText={(text) => setFormData({ ...formData, serviceAddress: text })}
              placeholder="Ex: 123 Main St, City, State 12345"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Billing Address</Text>
            <TextInput
              style={styles.input}
              value={formData.billingAddress}
              onChangeText={(text) => setFormData({ ...formData, billingAddress: text })}
              placeholder="Ex: 456 Oak Ave, City, State 12345"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Customer Service Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.customerServicePhone}
              onChangeText={(text) => handlePhoneChange('customerServicePhone', text)}
              placeholder="Ex: (555) 123-4567"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Emergency Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.emergencyPhone}
              onChangeText={(text) => handlePhoneChange('emergencyPhone', text)}
              placeholder="Ex: (555) 911-1234"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Website</Text>
            <TextInput
              style={styles.input}
              value={formData.website}
              onChangeText={(text) => setFormData({ ...formData, website: text })}
              placeholder="Ex: www.citygas.com"
              placeholderTextColor="#A9A9A9"
              keyboardType="url"
              autoCapitalize="none"
            />
          </View>

          {/* Portal Toggle - Moved after website */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your gas provider have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Billing Cycle</Text>
            <TouchableOpacity
              style={[styles.input, styles.dropdownButton]}
              onPress={() => setShowBillingCycleModal(true)}
            >
              <Text style={[styles.inputText, !formData.billingCycle && styles.placeholderText]}>
                {formData.billingCycle || 'Select billing cycle'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monthly Cost</Text>
            <TextInput
              style={styles.input}
              value={formData.monthlyCost ? `$${formData.monthlyCost}` : ''}
              onChangeText={handleCurrencyChange}
              placeholder="Ex: $75.50"
              placeholderTextColor="#A9A9A9"
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.toggleContainer}>
            <Text style={styles.toggleText}>Autopay Enabled</Text>
            <Switch
              trackColor={{ false: "#767577", true: "#29BFC0" }}
              thumbColor={formData.autopayEnabled ? "#fff" : "#f4f3f4"}
              ios_backgroundColor="#767577"
              onValueChange={(value) => setFormData({ ...formData, autopayEnabled: value })}
              value={formData.autopayEnabled}
            />
          </View>

          {formData.autopayEnabled && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Autopay Method</Text>
              <TouchableOpacity
                style={[styles.input, styles.dropdownButton]}
                onPress={() => setShowAutopayMethodModal(true)}
              >
                <Text style={[styles.inputText, !formData.autopayMethod && styles.placeholderText]}>
                  {formData.autopayMethod || 'Select autopay method'}
                </Text>
                <Image
                  source={require('../../../../assets/icons/down.png')}
                  style={styles.dropdownIcon}
                />
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Meter Number</Text>
            <TextInput
              style={styles.input}
              value={formData.meterNumber}
              onChangeText={(text) => setFormData({ ...formData, meterNumber: text })}
              placeholder="Ex: GM123456"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Shutoff Location</Text>
            <TextInput
              style={styles.input}
              value={formData.shutoffLocation}
              onChangeText={(text) => setFormData({ ...formData, shutoffLocation: text })}
              placeholder="Ex: Basement near water heater"
              placeholderTextColor="#A9A9A9"
              multiline
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Additional information..."
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity 
                style={styles.addAnotherButton} 
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.doneButton} 
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <TouchableOpacity style={[styles.submitButton, styles.buttonDisabled]} disabled>
                  <ActivityIndicator color="#FFFFFF" />
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.submitButton}
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>
                    {isEdit ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />

      {/* Billing Cycle Modal */}
      <Modal
        visible={showBillingCycleModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowBillingCycleModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Billing Cycle</Text>
              <TouchableOpacity
                onPress={() => setShowBillingCycleModal(false)}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {billingCycles.map((cycle) => (
                <TouchableOpacity
                  key={cycle}
                  style={styles.stateItem}
                  onPress={() => handleBillingCycleSelect(cycle)}
                >
                  <Text style={styles.stateItemText}>{cycle}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Autopay Method Modal */}
      <Modal
        visible={showAutopayMethodModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowAutopayMethodModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Autopay Method</Text>
              <TouchableOpacity
                onPress={() => setShowAutopayMethodModal(false)}
                style={styles.modalCloseButton}
              >
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {autopayMethods.map((method) => (
                <TouchableOpacity
                  key={method}
                  style={styles.stateItem}
                  onPress={() => handleAutopayMethodSelect(method)}
                >
                  <Text style={styles.stateItemText}>{method}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(120),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  vaultText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontWeight: '600',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginRight: ms(16),
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});